import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { Toaster } from 'react-hot-toast';
import { HamburgerMenu } from '@/components';
import { useTicketBooking } from '@/features/booking/hooks/useTicketBooking';
import { useLoadingContext, useAuth } from '@/hooks';

import {
  HeroSection,
  AddOnSelector,
  EventDetailsSection,
  BookingCategoryHero,
  FloatingGuestSelector,
  FloatingPriceSummary
} from '@/features/booking/components';
import AddOnDetailModal from '@/features/booking/components/AddOnDetailModal';
import { DesktopAllowedAreaSection } from './DesktopAllowedAreaSection';
import { PerkCarousel } from '@/features/booking/components/PerkCarousel';
import type { AddOnScheduleData } from '@/features/booking/types/ticketBookingTypes';
import { MenuSection } from './MenuSection';

const TicketBookingDesktop: React.FC = () => {
  const { eventBookingCategoryId } = useParams<{
    eventBookingCategoryId: string;
  }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { startLoading, stopLoading } = useLoadingContext();
  const { isGuest, isAuthenticated } = useAuth();

  // Extract cartType and cartItemId from query parameters
  const cartTypeParam = searchParams.get('cartType');
  const initialCartTab: 'main' | 'guest' | null =
    cartTypeParam === 'guest' || cartTypeParam === 'main' ? cartTypeParam : null;
  const cartItemIdParam = searchParams.get('cartItemId');

  const {
    eventSchedule,
    eventBookingCategory,
    guestCount,
    addOnIdsPerGuest,
    selectedAddOns,
    loading,
    error,
    addingToCart,
    priceBreakdown,
    setGuestCount,
    handleAddOnToggle,
    setAddOnQuantityForGuest,
    addToCart,
    updateCart,
    deleteCartItem,
    incompleteCarts,
    selectedCartTab,
    currentCart,
    isLoadingCarts,
    isUpdatingCart,
    hasCartItemForEBC,
    hasCartChanges,
    switchCartTab,
    getItemCountForTab,
  } = useTicketBooking(eventBookingCategoryId || '', initialCartTab, cartItemIdParam);

  // Get club handle
  const clubHandle = eventSchedule?.event?.business?.handle ||
    eventSchedule?.event?.business?.handle ||
    eventBookingCategory?.booking_category?.business?.handle || '';

  const pageCurrency = eventBookingCategory?.currency;
  const timezone = eventSchedule?.timezone_id;
  
  // Log warning if currency is missing from API
  if (!pageCurrency && eventBookingCategory) {
    console.warn('TicketBookingDesktop: currency missing from eventBookingCategory');
  }

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAddOn, setSelectedAddOn] = useState<AddOnScheduleData | null>(null);

  // Note: Removed auth redirect to allow unauthenticated users to use ticket booking page

  // Redirect to table booking page if this is a table booking
  useEffect(() => {
    if (eventBookingCategory && eventBookingCategory.booking_category.is_table_booking) {
      const cartTypeParam = searchParams.get('cartType');
      const queryString = cartTypeParam ? `?cartType=${cartTypeParam}` : '';
      navigate(`/booking/reservations/${eventBookingCategoryId}${queryString}`, { replace: true });
    }
  }, [eventBookingCategory, eventBookingCategoryId, navigate, searchParams]);

  // Trigger loading when data is loading
  useEffect(() => {
    if (loading) {
      startLoading('ticket-booking-data');
    } else {
      stopLoading('ticket-booking-data');
    }

    return () => {
      stopLoading('ticket-booking-data');
    };
  }, [loading, startLoading, stopLoading]);



  const handleAddToCart = async () => {
    // Handle delete case: when guest count is 0 and there's a cart item
    if (currentCart && hasCartItemForEBC && guestCount === 0) {
      // Delete the cart item using delete API
      await deleteCartItem();
      // Cart count will auto-refresh via header's stale-while-revalidate
      return;
    }

    // Check if we should update cart or add to cart
    if (currentCart && hasCartItemForEBC && hasCartChanges) {
      // Update existing cart item
      await updateCart();
      // Cart count will auto-refresh via header's stale-while-revalidate
    } else if (currentCart && hasCartItemForEBC && !hasCartChanges) {
      // No changes - navigate to cart page
      navigate(`/carts/${currentCart.id}`);
    } else {
      // Add new item to cart (either no cart, or cart exists but no item for this EBC)
      await addToCart();
      // Cart count will auto-refresh via header's stale-while-revalidate
    }
  };

  const handleBack = () => {
    if (eventSchedule?.event?.business?.handle && eventSchedule?.event?.id) {
      navigate(`/venues/${eventSchedule.event.business.handle}/events/${eventSchedule.event.id}`);
    } else {
      navigate(-1);
    }
  };

  const handleAddOnClick = (addOn: AddOnScheduleData) => {
    setSelectedAddOn(addOn);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedAddOn(null);
  };

  const handleModalToggle = (addOnId: string, isSelected: boolean, guestIndex?: number) => {
    handleAddOnToggle(addOnId, isSelected, guestIndex);
  };

  const handleQuantityChange = (addOnId: string, guestIndex: number, quantity: number) => {
    setAddOnQuantityForGuest(addOnId, guestIndex, quantity);
  };

  if (loading) {
    return (
      <div className="bg-black min-h-screen">
        {/* Loading is handled by the global loader */}
      </div>
    );
  }

  if (error || !eventSchedule || !eventBookingCategory) {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Booking Not Found</h1>
          <p className="text-gray-400 mb-6">
            {error || 'The booking option you are looking for does not exist.'}
          </p>
          <button
            onClick={handleBack}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Only proceed if we have the required data
  if (!eventSchedule || !eventBookingCategory) {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Loading...</h1>
          <p className="text-gray-400">Please wait while we load the booking details.</p>
        </div>
      </div>
    );
  }

  const event = eventSchedule?.event;
  const business = eventBookingCategory?.booking_category?.business;

  return (
    <>
    <div className="bg-black min-h-screen text-white font-sans overflow-x-clip">
      <HamburgerMenu clubHandle={clubHandle} isAuthenticated={isAuthenticated} />
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: '#1f2937',
            color: '#fff',
            border: '1px solid #374151'
          },
          duration: 4000,
        }}
      />

      {/* Page Header (100vh on desktop) */}
      <div className="flex flex-col w-full relative md:min-h-[100dvh] md:min-h-[600px] 2xl:min-h-[700px] overflow-hidden bg-black">
        {/* Hero Section */}
        <div className="relative w-full md:flex-1 flex flex-col min-h-[300px] md:min-h-[20vh] lg:min-h-[25vh] md:max-h-[50vh] 2xl:max-h-[40vh]">
          {/* Default Hero size on mobile, absolute stretch on desktop */}
          <div className="md:hidden">
            <HeroSection event={event} business={business} />
          </div>
          <div className="hidden md:block absolute inset-0">
            <HeroSection event={event} business={business} className="w-full h-full" />
          </div>

          {/* Floating Back Button */}
          <button
            onClick={handleBack}
            className="absolute top-6 left-6 w-10 h-10 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:text-gray-200 transition-all duration-200 group z-10"
          >
            <svg
              className="w-5 h-5 transform group-hover:-translate-x-0.5 transition-transform duration-200"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>

        {/* Event Details */}
        <div className="shrink-0 relative z-10 w-full">
          <EventDetailsSection
            eventSchedule={eventSchedule}
            eventBookingCategory={eventBookingCategory}
          />
        </div>

        {/* Booking Category Hero */}
        <div className="shrink-0 relative z-10 w-full flex md:flex-none">
          <BookingCategoryHero eventBookingCategory={eventBookingCategory as any} currency={pageCurrency} />
        </div>
      </div>

      {/* Cart Loading State */}
      {isLoadingCarts && (
        <div className="w-full mx-auto px-6 md:px-8 lg:px-12 xl:px-20 2xl:px-[12vw] pt-4">
          <div className="flex items-center justify-center gap-3 py-4">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent"></div>
            <span className="text-gray-400 text-sm font-medium">Checking your cart...</span>
          </div>
        </div>
      )}

      {/* Cart Tabs - Conditional rendering based on three cases */}
      {!isLoadingCarts && incompleteCarts && incompleteCarts.length > 0 && (() => {
        const mainCart = incompleteCarts.find(c => !c.is_guest_cart);
        const guestCart = incompleteCarts.find(c => c.is_guest_cart);
        const mainCartHasItems = mainCart?.items && mainCart.items.length > 0;
        const guestCartHasItems = guestCart?.items && guestCart.items.length > 0;

        // First check: If neither cart has items for this EBC, don't show tabs at all
        if (!mainCartHasItems && !guestCartHasItems) {
          return null;
        }

        // Case 1: Both carts exist → Show both tabs
        // Case 2: Only guest cart exists → Show both tabs if user is authenticated, hide tabs if user is guest
        // Case 3: Only main cart exists OR no cart items → Don't show tabs
        // Show tabs if: guest cart exists AND guest cart has items AND (main cart has items OR user is not a guest)
        const showTabs = !!guestCart && guestCartHasItems && (mainCartHasItems || !isGuest);

        if (!showTabs) {
          return null;
        }

        // Determine if Main Cart tab should be shown
        // Show Main Cart tab if: main cart has items OR (guest cart exists AND user is not a guest)
        const showMainCartTab = mainCartHasItems || (guestCart && !isGuest);

        return (
          <div className="w-full mx-auto px-6 md:px-8 lg:px-12 xl:px-20 2xl:px-[12vw] pt-6">
            <div className="flex gap-2 border-b border-gray-700">
              {/* Main Cart Tab - Show if main cart has items OR if main cart exists and user is not a guest */}
              {showMainCartTab && (
                <button
                  onClick={() => switchCartTab('main')}
                  className={`px-6 py-3 font-medium transition-colors relative ${selectedCartTab === 'main'
                      ? 'text-white border-b-2 border-blue-500'
                      : 'text-gray-400 hover:text-gray-300'
                    }`}
                >
                  Main Cart{getItemCountForTab('main') > 0 ? ` (${getItemCountForTab('main')})` : ''}
                </button>
              )}

              {/* Guest Cart Tab - Always show when tabs are visible */}
              <button
                onClick={() => switchCartTab('guest')}
                className={`px-6 py-3 font-medium transition-colors relative ${selectedCartTab === 'guest'
                    ? 'text-white border-b-2 border-blue-500'
                    : 'text-gray-400 hover:text-gray-300'
                  }`}
              >
                Guest Cart{getItemCountForTab('guest') > 0 ? ` (${getItemCountForTab('guest')})` : ''}
              </button>
            </div>
          </div>
        );
      })()}

      {/* Main Content */}
      <main className="py-12 pb-48">
        <div className="w-full mx-auto px-6 md:px-8 lg:px-12 xl:px-20 2xl:px-[12vw] space-y-8">
          
          {/* Allowed Areas Section */}
          <DesktopAllowedAreaSection allowedAreas={eventBookingCategory?.booking_category?.allowed_areas || null} />

          {/* Perks Carousel Section */}
          <PerkCarousel perks={eventBookingCategory?.booking_category?.perks || []} />

          {/* Add-ons Selector */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <AddOnSelector
              addOns={eventBookingCategory?.add_on_schedules || []}
              selectedAddOns={selectedAddOns}
              onAddOnToggle={handleAddOnToggle}
              onAddOnClick={handleAddOnClick}
              guestCount={guestCount}
              addOnIdsPerGuest={addOnIdsPerGuest}
              currency={pageCurrency}
            />
          </motion.div>

          {/* Menu Section */}
          {eventBookingCategoryId && (
            <MenuSection 
              eventBookingCategoryId={eventBookingCategoryId} 
              currency={pageCurrency || 'GBP'} 
            />
          )}

        </div>
      </main>

      {/* Floating Guest Selector */}
      <FloatingGuestSelector
        guestCount={guestCount}
        onGuestCountChange={setGuestCount}
        basePrice={eventBookingCategory?.price || 0}
        isLoadingCarts={isLoadingCarts}
        hasCartItemForEBC={hasCartItemForEBC}
        currency={pageCurrency}
      />

      {/* Floating Price Summary - Only visible if price > 0 or has items in cart */}
      {(priceBreakdown.totalPrice > 0 || hasCartItemForEBC || (currentCart && currentCart.items && currentCart.items.length > 0)) && (
        <FloatingPriceSummary
          priceBreakdown={priceBreakdown}
          guestCount={guestCount}
          eventBookingCategory={eventBookingCategory}
          onAddToCart={handleAddToCart}
          addingToCart={addingToCart || isUpdatingCart}
          buttonText={currentCart && hasCartItemForEBC ? 'Update Cart' : 'Add to Cart'}
          isUpdateMode={!!(currentCart && hasCartItemForEBC)}
          onUpdateCart={currentCart && hasCartItemForEBC ? handleAddToCart : undefined}
          cartId={currentCart?.id || null}
          addOnCount={Object.values(selectedAddOns).reduce((sum, count) => sum + count, 0)}
          isLoadingCarts={isLoadingCarts}
          hasCartChanges={hasCartChanges}
          hasViewCartButton={!!(currentCart && currentCart.items && currentCart.items.length > 0)}
          hasCartItemForEBC={hasCartItemForEBC}
          isTableBooking={false}
          currency={pageCurrency}
          itemType={"TICKET"}
        />
      )}
      {/* Floating View Cart Button - Only show when cart has items, positioned below Update Cart button */}
      {currentCart && currentCart.items && currentCart.items.length > 0 && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          onClick={() => navigate(`/carts/${currentCart.id}`)}
          className="fixed bottom-8 right-3 md:right-4 lg:right-6 xl:right-10 2xl:right-[6vw] z-50 w-80 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl shadow-2xl flex items-center justify-center space-x-2 transition-all duration-200 border border-blue-500/50"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <span className="font-semibold">View Cart</span>
          <span className="bg-white text-blue-600 rounded-full px-2 py-0.5 text-xs font-bold">
            {currentCart.items.length}
          </span>
        </motion.button>
      )}

      {/* Add-on Detail Modal */}
      <AddOnDetailModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        addOn={selectedAddOn}
        isSelected={selectedAddOn ? selectedAddOns[selectedAddOn.add_on.id] > 0 : false}
        onToggle={handleModalToggle}
        onQuantityChange={handleQuantityChange}
        guestCount={guestCount}
        remainingInventory={selectedAddOn?.remaining_inventory ?? null}
        addOnIdsPerGuest={addOnIdsPerGuest}
        allAddOns={eventBookingCategory?.add_on_schedules || []}
        currency={pageCurrency}
        eventStartTime={eventSchedule?.start_time}
        earlyEntryDuration={eventSchedule?.early_entry_duration}
        timezone={timezone}
      />

    </div>
    </>
  );
};

export default TicketBookingDesktop;
